import { useState } from 'react';
import { Sheet } from '@/components/sheets/Sheet';
import { Icon } from '@/components/ui/Icon';
import { NeonButton } from '@/components/ui/NeonButton';
import { cn } from '@/lib/cn';
import {
  REPORT_REASONS,
  REPORT_REASON_LABEL,
  useReport,
  type ReportReason,
} from './useReport';

interface ReportSheetProps {
  open: boolean;
  onClose: () => void;
  reportedUserId: string | null;
  reportedHandle: string;
  onSuccess?: () => void;
}

export const ReportSheet = ({
  open,
  onClose,
  reportedUserId,
  reportedHandle,
  onSuccess,
}: ReportSheetProps) => {
  const { submit, submitting } = useReport();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [detail, setDetail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setReason(null);
    setDetail('');
    setError(null);
  };

  const handleSubmit = async () => {
    if (!reason || !reportedUserId) return;
    setError(null);
    const result = await submit({
      reportedUserId,
      reason,
      detail: detail.trim() || undefined,
      contextKind: 'profile',
    });
    if (!result.ok) {
      setError(result.error ?? 'Could not file report. Try again.');
      return;
    }
    reset();
    onClose();
    onSuccess?.();
  };

  return (
    <Sheet
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      eyebrow="Report profile"
      title={
        <>
          @{reportedHandle}<span className="text-pink">.</span>
        </>
      }
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="font-display text-[11px] font-bold uppercase tracking-widest text-text-muted hover:text-text-primary"
          >
            Cancel
          </button>
          <NeonButton
            type="button"
            size="md"
            disabled={!reason || submitting || !reportedUserId}
            onClick={handleSubmit}
            trailing={<Icon name="flag" size={14} />}
          >
            {submitting ? 'Sending' : 'Send report'}
          </NeonButton>
        </div>
      }
    >
      <p className="mb-5 text-xs text-text-secondary">
        A human reviews this within 24 hours. Keep it short and specific —
        the details below are private.
      </p>
      <ul className="mb-6 divide-y divide-white/[0.06] border-y border-white/[0.06]">
        {REPORT_REASONS.map((value) => {
          const selected = reason === value;
          return (
            <li key={value}>
              <button
                type="button"
                onClick={() => setReason(value)}
                className={cn(
                  'flex w-full items-center justify-between gap-4 py-3 text-left',
                  'transition-colors duration-fast ease-signal',
                  selected ? 'text-pink' : 'text-text-primary hover:text-pink',
                )}
              >
                <span className="font-display text-sm font-bold uppercase tracking-wide">
                  {REPORT_REASON_LABEL[value]}
                </span>
                <Icon
                  name={selected ? 'radio_button_checked' : 'radio_button_unchecked'}
                  size={16}
                />
              </button>
            </li>
          );
        })}
      </ul>
      <label className="block">
        <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          More detail — optional
        </span>
        <textarea
          value={detail}
          onChange={(e) => setDetail(e.target.value.slice(0, 1000))}
          rows={3}
          placeholder="what happened, when, and where you saw it."
          className={cn(
            'mt-2 w-full resize-none border border-white/[0.10] bg-charcoal/40 px-3 py-2 text-sm',
            'placeholder:text-text-muted focus:border-pink focus:outline-none',
          )}
        />
        <span className="mt-1 block text-right font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
          {detail.length} / 1000
        </span>
      </label>
      {error && (
        <p className="mt-3 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
          — {error}
        </p>
      )}
    </Sheet>
  );
};
